import { cn } from '@nextui-org/react'
import { IoMdDownload } from '@react-icons/all-files/io/IoMdDownload'
import { PiMicrosoftExcelLogoLight } from '@react-icons/all-files/pi/PiMicrosoftExcelLogoLight'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { DownloadDocumentDropdown } from '~/components/common/dropdowns/DownloadDocumentDropdown'
import { TaskStatusDetail, TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { ModalId, useModalController } from '~/hooks/useModal'
import {
  AgentTaskStatus,
  CommandType,
  DocumentInlineComment,
  ExecutionMode,
  MigrationSelectedEndpoint,
  TaskStatusAndRequestResponseTuple,
  VerificationMode,
  agentTaskStatusFromJSON
} from '~/protos/agents'
import { submitMigrationEndpoints } from '~/services/taskService'
import { MigrationEndpoint } from '~/types/migration'
import { csvToMarkdown } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { CodePlanViewer } from '../code-plan/Planner'
import { DocMarkdown } from '../documents'
import { Img as DocMarkdownImg } from '../documents/img'
import { MigrationEndpointSelector } from '../migration/MigrationEndpointSelector'
import { MigrationRiskSectionEditor } from '../migration/MigrationRiskSectionEditor'
import { SelectedEndpointsDisplay } from '../migration/SelectedEndpointsDisplay'
import TaskResultsView from '../task/TaskResultsView'

export interface TaskStatusDetailModalProps extends TaskStatusDetail {
  title: string
  index: number
  taskStatus: AgentTaskStatus
}

export const getCodePlanFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.codePlan
}

export const getCodePlanFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.response?.[0]?.responseContentStream?.codePlan
}

export const getDiffFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.diff || []
}

export const getDiffFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.response?.[0]?.responseContentStream?.diff || []
}

export const getDeltaContentFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.response?.[0]?.responseContentStream?.deltaContent || ''
}

export const getMigrationDocumentDiffsFromTupleResponse = (
  tuple: TaskStatusAndRequestResponseTuple
) => {
  return tuple.response?.[0]?.responseContentStream?.migrationDocOutput?.diffs || []
}

export const getDocumentFromTupleResponse = (tuple: TaskStatusAndRequestResponseTuple) => {
  const stream = tuple.response?.[0]?.responseContentStream
  const document = stream?.document

  if (document != null) {
    for (const [key, value] of Object.entries(document!)) {
      if (value == null || value == null || value === '') {
        continue
      }

      let md = ''
      if (key === 'mappingSheetCsv') {
        md = csvToMarkdown(value)
      } else {
        md = value
      }
      return md
    }
  }

  const migrationDoc = stream?.migrationDocOutput
  if (migrationDoc) {
    if (migrationDoc.docMd) return migrationDoc.docMd
    if (migrationDoc.endpointsMd) return migrationDoc.endpointsMd
    if (migrationDoc.apiSpec?.length) return migrationDoc.apiSpec.join('\n\n')
    if (migrationDoc.migDocs?.length) return migrationDoc.migDocs.join('\n\n')
  }

  return stream?.munitScenarios?.mdText || stream?.deltaContent || undefined
}

export const getCommentFromTupleRequest = (tuple: TaskStatusAndRequestResponseTuple) => {
  return tuple.request?.documentInlineComments?.[0] as DocumentInlineComment
}

export const getDocumentWithCommentFromTupleRequest = (
  tuples: TaskStatusAndRequestResponseTuple[],
  index: number
) => {
  const generatedTuple = tuples.findLast((tuple, tupleIndex) => {
    if (tupleIndex > index) {
      return false
    }
    return tuple.taskStatus === AgentTaskStatus.DOCUMENT_GENERATED
  })
  const comment = getCommentFromTupleRequest(tuples[index])
  return {
    markdown: getDocumentFromTupleResponse(generatedTuple!)!,
    comment: comment
  } as const
}

export const getNewCodePlanFromTuples = (
  tuples: TaskStatusAndRequestResponseTuple[],
  index: number
) => {
  const tuple = tuples[index]
  const completedTuple = tuples.findLast((tuple, tupleIndex) => {
    if (tupleIndex > index) {
      return false
    }
    return tuple.taskStatus === AgentTaskStatus.PLANNING_COMPLETED
  })
  if (completedTuple) {
    const originalCodePlan = getCodePlanFromTupleResponse(completedTuple)
    const editedCodePlan = getCodePlanFromTupleRequest(tuple)
    if (originalCodePlan && editedCodePlan) {
      const newCodePlan = JSON.parse(JSON.stringify(originalCodePlan))
      newCodePlan.fileMultiSelect!.selectedFileNames =
        editedCodePlan.fileMultiSelect!.selectedFileNames
      return newCodePlan
    }
  }
}

function highlightLines(markdown: string, startLine: number, endLine: number): string {
  const lines = markdown.split('\n')

  if (startLine > 0 && startLine <= lines.length) {
    const highlightCount = endLine - startLine + 1
    lines[startLine - 1] = `# [!code warning:${highlightCount}]\n${lines[startLine - 1]}`
  }

  return lines.join('\n')
}

function coerceDisplayString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

function parseJsonMetadata(jsonMetadata?: string): Record<string, unknown> {
  if (!jsonMetadata?.trim()) return {}

  try {
    const parsed = JSON.parse(jsonMetadata)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

function formatTimeoutSeconds(value: unknown): string | undefined {
  const timeout =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(timeout) ? `${timeout} seconds` : undefined
}

function getMavenCommandDetailRows(request: ReturnType<typeof getTaskRequest>) {
  const runtimeSettings = request?.runtimeSettings
  const metadata = parseJsonMetadata(request?.jsonMetadata)

  return [
    ['Java', runtimeSettings?.javaVersion],
    ['Maven', runtimeSettings?.mavenVersion],
    ['Mule Runtime', runtimeSettings?.muleRuntimeVersion],
    ['Working directory', coerceDisplayString(metadata.working_directory) || '.'],
    ['Timeout', formatTimeoutSeconds(metadata.timeout_seconds)],
    ['Repository upload', coerceDisplayString(metadata.repo_s3_key)],
    [
      'settings.xml',
      runtimeSettings?.settingsXmlRawTxt?.trim() ? 'Supplied in task request' : 'Runner default'
    ],
    [
      'settings-security.xml',
      runtimeSettings?.settingsSecurityXmlRawTxt?.trim()
        ? 'Supplied in task request'
        : 'Runner default'
    ]
  ].flatMap(([label, value]) => {
    const displayValue = coerceDisplayString(value)
    return displayValue ? [{ label, value: displayValue }] : []
  })
}

export function TaskStatusDetailModal({ index, taskStatus }: TaskStatusDetailModalProps) {
  const { task } = useTask()
  const { workspaceUuid, uuid } = useParams<{ workspaceUuid: string; uuid: string }>()
  const { closeModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)
  const [selectedEndpoints, setSelectedEndpoints] = useState<MigrationEndpoint[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const tuples = task?.details.orderedStateResponses ?? []
  const tuple = tuples[index]
  const isDiffTaskStatus = TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DIFF
  const isEndpointSelection =
    TaskStatuses[taskStatus].detailType === TaskStatusDetailType.ENDPOINT_SELECTION
  const isRiskReview = TaskStatuses[taskStatus].detailType === TaskStatusDetailType.RISK_REVIEW

  // Get current task status to determine if submit button should be shown
  const currentTaskStatus = task?.status ? agentTaskStatusFromJSON(task.status) : null
  const canSubmitEndpoints = currentTaskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED

  const isPlugin = useCompactViewport()
  let text: string | undefined = undefined
  let className = isDiffTaskStatus
    ? isPlugin
      ? 'w-full max-w-[calc(100vw-24px)] min-h-[400px] max-h-[calc(100vh-32px)]'
      : 'min-w-[1200px] min-h-[700px] max-h-[900px]'
    : isEndpointSelection
      ? isPlugin
        ? 'w-full max-w-[calc(100vw-24px)] min-h-[400px] max-h-[calc(100vh-32px)]'
        : 'w-full max-w-4xl min-h-[700px] max-h-[90vh]'
      : isPlugin
        ? 'w-full max-w-[calc(100vw-24px)] min-h-[200px] max-h-[calc(100vh-32px)]'
        : 'w-auto min-h-[400px] max-h-[600px]'

  const isEndpointDisplay = taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED
  if (isEndpointDisplay) {
    className = isPlugin
      ? 'w-full max-w-[calc(100vw-24px)] min-h-[200px] max-h-[calc(100vh-32px)]'
      : 'w-auto max-w-[800px] min-h-[300px] max-h-[600px]'
  }
  let description: string | undefined = undefined

  const renderUploadedImages = (task?: TaskObject) => {
    const images = getTaskRequest(task)?.command?.images || []

    return images.length > 0 ? (
      <div className='flex flex-col mt-4'>
        <div className='label font-semibold text-xs text-default-700 mb-2'>Images Uploaded:</div>
        <div className='flex flex-wrap gap-2'>
          {images.map((image, index) => {
            if (!image.imageBase64Content || !image.imageExtension) {
              return null
            }
            const imageUrl = `data:image/${image.imageExtension};base64,${image.imageBase64Content}`
            return (
              <div key={index} className='border rounded p-1'>
                <DocMarkdownImg
                  src={imageUrl}
                  alt={`uploaded image ${index + 1}`}
                  className='inline-flex w-[100px] h-[100px] rounded object-cover'
                />
              </div>
            )
          })}
        </div>
      </div>
    ) : null
  }

  const renderUploadedSpreadsheets = (task?: TaskObject) => {
    const spreadsheets = getTaskRequest(task)?.mappingSheet || []

    const handleDownload = (spreadsheet: { id: string; base64EncodedXlsx: string }) => {
      const blob = new Blob([Buffer.from(spreadsheet.base64EncodedXlsx, 'base64')], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = spreadsheet.id
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }

    return spreadsheets.length > 0 ? (
      <div className='flex flex-col gap-2'>
        <div className='label font-semibold text-xs text-default-700'>Spreadsheet Uploaded:</div>
        <div className='flex flex-wrap gap-2'>
          {spreadsheets.map((spreadsheet, index) => {
            const file = new File([spreadsheet.base64EncodedXlsx], spreadsheet.id)

            return (
              <div key={index} className='w-full flex items-center justify-between mb-4'>
                <div
                  className={cn(
                    'border border-[#d4dce2] rounded-lg flex items-center min-w-[250px] flex-1',
                    'overflow-hidden text-ellipsis'
                  )}
                >
                  <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
                    <PiMicrosoftExcelLogoLight size={32} color='#6f7987' />
                  </div>
                  <div
                    className={cn(
                      'ml-2 whitespace-nowrap text-sm text-default-700 max-w-[70%]',
                      'overflow-hidden text-ellipsis'
                    )}
                  >
                    {file.name}
                  </div>
                </div>

                <div
                  className={cn(
                    'button-submit bg-white text-default-600',
                    'px-[10px] rounded-lg cursor-pointer',
                    'border border-[#d4dce2] select-none h-8 hover:bg-light3',
                    'disabled:cursor-not-allowed',
                    'w-12 h-12 ml-2 flex items-center justify-center'
                  )}
                  onClick={() => handleDownload(spreadsheet)}
                >
                  <IoMdDownload size={16} className='w-[16px] h-[16px]' />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    ) : null
  }

  const emptyContentLabels: Partial<Record<AgentTaskStatus, string>> = {
    [AgentTaskStatus.MIGRATION_API_SPEC_GENERATED]: 'No API spec.',
    [AgentTaskStatus.MIGRATION_CODE_GENERATED]: 'No generated code.',
    [AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED]: 'No document.',
    [AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED]: 'No document.',
    [AgentTaskStatus.REFACTOR_API_SPECS_GENERATED]: 'No API spec.',
    [AgentTaskStatus.REFACTOR_API_PROPOSED]: 'No API proposal.',
    [AgentTaskStatus.REFACTOR_CODE_GENERATED]: 'No generated code.',
    [AgentTaskStatus.DOCUMENT_GENERATED]: 'No document.',
    [AgentTaskStatus.MUNIT_SCENARIO_GENERATED]: 'No scenarios.',
    [AgentTaskStatus.STEPS_GENERATED]: 'No steps.',
    [AgentTaskStatus.STEPS_UPDATED]: 'No steps.'
  }

  const renderEmptyContent = (status: AgentTaskStatus) => {
    const label = emptyContentLabels[status] ?? 'No content available.'
    return (
      <div className='flex flex-1 items-stretch justify-center p-4 w-full'>
        <div className='flex flex-1 items-center justify-center w-full rounded-lg bg-default-100 border border-default-200'>
          <p className='text-foreground-400 text-sm'>{label}</p>
        </div>
      </div>
    )
  }

  const renderDetail = () => {
    if (taskStatus === AgentTaskStatus.SUBMITTED) {
      if (!task || !task.details) {
        return (
          <div className='flex flex-col items-center justify-center p-8'>
            <div className='text-default-500 text-sm'>
              Task details are not available yet. Please try again in a moment.
            </div>
          </div>
        )
      }

      const request = getTaskRequest(task)
      const commandType = request?.command?.commandType
      const flowName = request?.command?.commandString

      const isMunitTaskOrCoder =
        commandType === CommandType.MUNIT_TEST_CREATION ||
        commandType === CommandType.GENERATE_SIMPLE_CODE_PR ||
        commandType === CommandType.MULE_VERSION_UPGRADE

      if (isMunitTaskOrCoder && request) {
        return (
          <>
            <div className='label font-semibold text-xs text-default-700 mb-[10px]'>
              Task Details:
            </div>

            {/* Flow Name / Command */}
            <div className='mb-4'>
              <div className='label font-semibold text-xs text-default-600 mb-1'>
                {commandType === CommandType.MUNIT_TEST_CREATION ? 'Flow Name:' : 'Command:'}
              </div>
              <div className='text-sm text-default-700 bg-default-100 p-2 rounded'>
                <DocMarkdown className='pl-0' text={flowName || 'Not specified'} />
              </div>
            </div>

            {/* Run Verification */}
            {request.command?.verificationMode !== undefined &&
            request.command.verificationMode !== 0 &&
            request.command?.executionMode !== ExecutionMode.ASK ? (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Run Verification:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded'>
                  <span className='font-medium'>
                    {request.command.verificationMode === VerificationMode.VERIFY
                      ? 'true'
                      : 'false'}
                  </span>
                </div>
              </div>
            ) : null}

            {/* Runtime Versions */}
            {request.runtimeSettings && (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Runtime Versions:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded'>
                  {request.runtimeSettings.javaVersion && (
                    <div className='mb-1'>
                      <span className='font-medium'>Java:</span>{' '}
                      {request.runtimeSettings.javaVersion}
                    </div>
                  )}
                  {request.runtimeSettings.mavenVersion && (
                    <div className='mb-1'>
                      <span className='font-medium'>Maven:</span>{' '}
                      {request.runtimeSettings.mavenVersion}
                    </div>
                  )}
                  {request.runtimeSettings.muleRuntimeVersion && (
                    <div>
                      <span className='font-medium'>Mule Runtime:</span>{' '}
                      {request.runtimeSettings.muleRuntimeVersion}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* VM Arguments */}
            {request.vmArgs && (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  VM Arguments:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded font-mono'>
                  {request.vmArgs}
                </div>
              </div>
            )}

            {/* Coverage Settings - Only for Munit */}
            {commandType === CommandType.MUNIT_TEST_CREATION && (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Test Configuration:
                </div>
                <div className='text-sm text-default-700'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span>• Coverage Enabled:</span>
                    <span className='font-medium'>
                      {request.munitEnableCoverage ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span>• Number of Scenarios:</span>
                    <span className='font-medium'>{request.munitNumScenarios || 1}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings XML */}
            {request.runtimeSettings?.settingsXmlRawTxt && (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Settings.xml:
                </div>
                <div className='text-xs text-default-700 bg-default-100 p-2 rounded font-mono overflow-auto max-h-40'>
                  <pre>{request.runtimeSettings.settingsXmlRawTxt}</pre>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {request.munitNotes && (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Additional Notes:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded whitespace-pre-wrap'>
                  {request.munitNotes}
                </div>
              </div>
            )}
            {renderUploadedImages(task)}
            {renderUploadedSpreadsheets(task)}
          </>
        )
      } else {
        // Default behavior for non-Munit tasks
        const mavenCommandDetailRows =
          commandType === CommandType.MVN_CMD ? getMavenCommandDetailRows(request) : []
        text = flowName || 'No description provided'
        // Code blocks are too rigid on narrow widths. Use plain markdown there.
        text = isPlugin ? text : `\`\`\`\n${text}\n\`\`\``
        description = 'Task description:'
        return (
          <>
            {description ? (
              <div className='label font-semibold text-xs text-default-700 mb-[10px]'>
                <DocMarkdown className='pl-0' text={description} />
              </div>
            ) : null}
            {/* Run Verification */}
            {request?.command?.verificationMode !== undefined &&
            request.command.verificationMode !== 0 &&
            request?.command?.executionMode !== ExecutionMode.ASK ? (
              <div className='mb-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Run Verification:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded'>
                  <span className='font-medium'>
                    {request.command.verificationMode === VerificationMode.VERIFY
                      ? 'true'
                      : 'false'}
                  </span>
                </div>
              </div>
            ) : null}
            <DocMarkdown
              className={isPlugin ? 'flex-1 pl-0 break-words' : 'flex-1 pl-0'}
              text={text}
            />
            {mavenCommandDetailRows.length > 0 ? (
              <div className='mb-4 mt-4' data-testid='maven-command-task-details'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Maven command details:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded'>
                  {mavenCommandDetailRows.map(({ label, value }) => (
                    <div key={label} className='mb-1 last:mb-0'>
                      <span className='font-medium'>{label}:</span> {value}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {/* userNotes: ITA deployment line + additional notes, and other non-Munit commands */}
            {request?.userNotes?.trim() ? (
              <div className='mb-4 mt-4'>
                <div className='label font-semibold text-xs text-default-600 mb-1'>
                  Additional notes:
                </div>
                <div className='text-sm text-default-700 bg-default-100 p-2 rounded whitespace-pre-wrap'>
                  {request.userNotes}
                </div>
              </div>
            ) : null}
            {renderUploadedImages(task)}
            {renderUploadedSpreadsheets(task)}
          </>
        )
      }
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.PLAN) {
      if (AgentTaskStatus.PLANNING_COMPLETED === taskStatus) {
        return <CodePlanViewer codePlan={getCodePlanFromTupleResponse(tuple)} />
      } else {
        const editedCodePlan = getNewCodePlanFromTuples(tuples, index)
        return editedCodePlan ? (
          <CodePlanViewer codePlan={editedCodePlan} />
        ) : (
          <CodePlanViewer codePlan={getCodePlanFromTupleRequest(tuple)} />
        )
      }
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DIFF) {
      // Migration document statuses: render content directly instead of via TaskResultsView
      // (TaskResultsView uses Radix Tabs which requires triggers to display content)
      const migrationDocStatuses = [
        AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
        AgentTaskStatus.MIGRATION_API_SPEC_GENERATED,
        AgentTaskStatus.MIGRATION_CODE_GENERATED
      ]
      if (migrationDocStatuses.includes(taskStatus)) {
        const migrationDoc = tuple?.response?.[0]?.responseContentStream?.migrationDocOutput
        const diffs = migrationDoc?.diffs || []
        const hasMapContent =
          Object.keys(migrationDoc?.migDocsMap || {}).length > 0 ||
          Object.keys(migrationDoc?.migDocsRiskMap || {}).length > 0
        const hasCodeGenSummary = !!migrationDoc?.codeGenSummary?.trim()

        if (diffs.length > 0 || hasMapContent || hasCodeGenSummary) {
          return (
            <div className='flex h-[75vh] w-full rounded p-4'>
              <TaskResultsView
                key={`task-results-${index}`}
                index={index}
                readOnly
                showAgentProgress={false}
              />
            </div>
          )
        }

        const markdown =
          migrationDoc?.docMd ||
          migrationDoc?.endpointsMd ||
          (migrationDoc?.apiSpec?.length ? migrationDoc.apiSpec.join('\n\n') : '') ||
          (migrationDoc?.migDocs?.length ? migrationDoc.migDocs.join('\n\n') : '') ||
          getDeltaContentFromTupleResponse(tuple) ||
          ''

        if (!markdown) {
          return (
            <div className='flex h-[75vh] w-full rounded p-4'>{renderEmptyContent(taskStatus)}</div>
          )
        }

        return (
          <div className='flex flex-col gap-2'>
            <DownloadDocumentDropdown content={markdown} />
            <DocMarkdown className='flex-1 px-2' text={markdown} />
          </div>
        )
      }

      if (taskStatus === AgentTaskStatus.DIFF_COMMENT_ADDED) {
        const generalDiffComment = (tuple as { request?: { generalDiffComment?: string } })?.request
          ?.generalDiffComment

        return (
          <div
            className={`flex flex-col items-center justify-center ${isPlugin ? 'p-3 w-full' : 'p-8 min-w-[400px] max-w-[600px]'}`}
          >
            <div
              className={`w-full bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl shadow-sm ${isPlugin ? 'p-4' : 'p-6'}`}
            >
              <div className='flex items-center gap-3 mb-4'>
                <div className='flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full'>
                  <svg
                    className='w-5 h-5 text-amber-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z'
                    />
                  </svg>
                </div>
                <div>
                  <h3 className='text-lg font-semibold text-amber-900'>Feedback Submitted</h3>
                  <p className='text-sm text-amber-700'>Review comments on the generated diff</p>
                </div>
              </div>
              {generalDiffComment ? (
                <div className='bg-white/60 rounded-lg p-4 border border-amber-100'>
                  <div className='text-sm text-amber-900 whitespace-pre-wrap leading-relaxed'>
                    {generalDiffComment}
                  </div>
                </div>
              ) : (
                <div className='text-sm text-amber-600 italic'>No comment provided</div>
              )}
            </div>
          </div>
        )
      }

      // Container handles determining types, defaultView, and documentListProps based on command type
      // showAgentProgress=false since this is a detail view for a specific status
      return (
        <div
          className={cm(
            'flex rounded',
            isPlugin ? 'h-full min-h-0 w-full p-1' : 'h-[82vh] w-full p-4'
          )}
        >
          <TaskResultsView
            key={`task-results-${index}`}
            index={index}
            readOnly
            showAgentProgress={false}
          />
        </div>
      )
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.DOC) {
      if (AgentTaskStatus.DOCUMENT_UPDATED === taskStatus) {
        const { markdown, comment } = getDocumentWithCommentFromTupleRequest(tuples, index)
        const highlighted = highlightLines(markdown, comment.startLineNumber, comment.endLineNumber)
        return (
          <>
            <div className='label font-semibold text-xs text-default-700 my-[15px] mt-[5px]'>
              User comment:
            </div>
            <div
              className={
                isPlugin
                  ? 'text-default-700 border border-outline rounded p-2 whitespace-break-spaces break-words'
                  : 'text-default-700 border border-outline rounded p-2 whitespace-break-spaces'
              }
            >
              {comment.comment}
            </div>
            <DocMarkdown text={highlighted} className='flex-1 px-2' />
          </>
        )
      } else if (AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED === taskStatus) {
        // Handle display of selected endpoints for MIGRATION_ENDPOINTS_SELECTED
        // Read from request.selectedEndpoints (preferred) with fallback to jsonMetadata for legacy tasks
        const selectedEndpointsTuple = tuples.findLast(
          tuple => tuple.taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED
        )

        let endpoints: MigrationEndpoint[] = []

        const requestSelectedEndpoints = selectedEndpointsTuple?.request?.selectedEndpoints
        if (requestSelectedEndpoints && requestSelectedEndpoints.length > 0) {
          // New format: read from request.selectedEndpoints
          endpoints = requestSelectedEndpoints.map(endpoint => ({
            name: endpoint.name,
            category: endpoint.category,
            file_loc: endpoint.filePath
          }))
        } else if (selectedEndpointsTuple?.response?.[0]?.responseContentStream?.jsonMetadata) {
          // Legacy fallback: read from response.jsonMetadata.mig_obj.endpoints
          // The mig_obj stored in response contains only selected endpoints
          try {
            const jsonMetadata = JSON.parse(
              selectedEndpointsTuple.response[0].responseContentStream.jsonMetadata
            )
            const migObj = JSON.parse(jsonMetadata.mig_obj)

            if (migObj?.endpoints) {
              endpoints = migObj.endpoints.map(
                (endpoint: { name: string; category: string; file_loc: string }) => ({
                  name: endpoint.name,
                  category: endpoint.category,
                  file_loc: endpoint.file_loc
                })
              )
            }
          } catch (error) {
            console.error('Failed to parse selected endpoints from response:', error)
          }
        } else if (task?.details.originalRequest?.requestFromApi) {
          // Older API/MCP auto-selection tuples did not persist request.selectedEndpoints.
          // Those requests select every identified endpoint, so render that source.
          const identifiedEndpointsTuple = tuples.findLast(
            tuple => tuple.taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED
          )
          const identifiedEndpoints =
            identifiedEndpointsTuple?.response?.[0]?.responseContentStream?.identifiedEndpoints

          if (identifiedEndpoints && identifiedEndpoints.length > 0) {
            endpoints = identifiedEndpoints.map(endpoint => ({
              name: endpoint.name,
              category: endpoint.category,
              file_loc: endpoint.filePath
            }))
          }
        }

        return <SelectedEndpointsDisplay endpoints={endpoints} />
      } else {
        const markdown = getDocumentFromTupleResponse(tuple) as string
        if (!markdown) {
          return renderEmptyContent(taskStatus)
        }
        return (
          <div className='flex flex-col gap-2'>
            <DownloadDocumentDropdown content={markdown} />
            <DocMarkdown className='flex-1 px-2' text={markdown} />
          </div>
        )
      }
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.ENDPOINT_SELECTION) {
      // Handle endpoint selection for MIGRATION_ENDPOINTS_IDENTIFIED
      // Extract endpoints from identifiedEndpoints field in the response (new format)
      // with fallback to jsonMetadata for legacy tasks
      const identifiedEndpoints = tuple.response?.[0]?.responseContentStream?.identifiedEndpoints
      const jsonMetadata = tuple.response?.[0]?.responseContentStream?.jsonMetadata
      let endpoints: MigrationEndpoint[] = []

      if (identifiedEndpoints && identifiedEndpoints.length > 0) {
        // New format: use identifiedEndpoints directly
        endpoints = identifiedEndpoints.map(endpoint => ({
          name: endpoint.name,
          category: endpoint.category,
          file_loc: endpoint.filePath
        }))
      } else if (jsonMetadata) {
        // Legacy fallback: parse from jsonMetadata (double-encoded JSON)
        // Migration Accelerator format: { mig_obj: "{...}" } -> mig_obj.endpoints
        try {
          const parsedMetadata = JSON.parse(jsonMetadata)
          const migrationObj = parsedMetadata.mig_obj ? JSON.parse(parsedMetadata.mig_obj) : null

          if (migrationObj?.endpoints) {
            endpoints = migrationObj.endpoints.map(
              (endpoint: { name: string; category: string; file_loc: string }) => ({
                name: endpoint.name,
                category: endpoint.category,
                file_loc: endpoint.file_loc
              })
            )
          }
        } catch (e) {
          console.error('Failed to parse migration endpoints:', e)
        }
      }

      const handleEndpointSelection = (newSelectedEndpoints: MigrationEndpoint[]) => {
        setSelectedEndpoints(newSelectedEndpoints)
      }

      const handleSubmit = async (selectedEndpointsForSubmission: MigrationSelectedEndpoint[]) => {
        if (!uuid) {
          console.error('No conversation UUID available')
          return
        }

        if (!workspaceUuid) {
          console.error('No workspace UUID available')
          return
        }

        if (!task?.task_id) {
          console.error('No task ID available')
          BetterToast.error('Task is still loading. Please try again.')
          return
        }

        setIsSubmitting(true)
        try {
          await submitMigrationEndpoints({
            workspaceUuid,
            uuid,
            taskId: task.task_id,
            selectedEndpoints: selectedEndpointsForSubmission
          })
          closeModal()

          // Optionally show success feedback like diff comments do
          // BetterToast.success('Endpoints submitted successfully')
        } catch (error) {
          console.error('Failed to submit endpoints:', error)
        } finally {
          setIsSubmitting(false)
        }
      }

      return (
        <MigrationEndpointSelector
          endpoints={endpoints}
          selectedEndpoints={selectedEndpoints}
          onSelectionChange={handleEndpointSelection}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          showSubmitButton={canSubmitEndpoints}
        />
      )
    } else if (TaskStatuses[taskStatus].detailType === TaskStatusDetailType.RISK_REVIEW) {
      if (!task?.task_id || !workspaceUuid || !uuid) {
        return null
      }

      return (
        <MigrationRiskSectionEditor
          tuples={tuples}
          index={index}
          taskId={task.task_id}
          workspaceUuid={workspaceUuid}
          uuid={uuid}
          currentTaskStatus={currentTaskStatus}
          onClose={closeModal}
        />
      )
    }
    // NOTE: the Azure deploy panel renders inline in the task timeline
    // (TaskProgressMessageView), not as a modal — so DEPLOY has no branch here.
  }

  if (isDiffTaskStatus) {
    return renderDetail()
  }

  if (isEndpointSelection || isRiskReview) {
    // For endpoint selection / risk review / deploy, render the component
    // directly without the modal wrapper (the deploy modal is its own CurieModal).
    return renderDetail()
  }

  return (
    <div className={cm('flex w-full flex-col h-full', isPlugin ? 'p-1' : 'p-4', className)}>
      <div
        className={
          isPlugin
            ? 'flex flex-col h-[calc(100%-50px)] min-h-0 overflow-y-auto px-2'
            : 'flex flex-col h-[calc(100%-50px)] min-h-[300px] overflow-y-scroll px-[20px]'
        }
      >
        {renderDetail()}
      </div>
    </div>
  )
}
